import { GetterTree } from 'vuex';
import moment from 'moment';
import { CalendarState } from './types';
import { RootState } from '@/store/types';
import { EventResource } from '@/api/requests/calendar/types';
import { UserResource } from '@/api/requests/types';

export const getters: GetterTree<CalendarState, RootState> = {
    /**
     * Check if calendar module is enabled.
     * @param state
     * @param nlGetters
     * @param rootState
     * @param rootGetters
     */
    calendarModuleEnabled: (state, nlGetters, rootState, rootGetters): boolean => {
        return rootGetters.permissionEnabled('calendars');
    },

    /**
     * Check if user can view all events.
     * @param state
     * @param nlGetters
     * @param rootState
     * @param rootGetters
     */
    canViewAnyEvent: (state, nlGetters, rootState, rootGetters): boolean => {
        return rootGetters.permissionEnabled('events.viewAny');
    },

    /**
     * Check if calendar module is enabled.
     * @param state
     * @param nlGetters
     * @param rootState
     * @param rootGetters
     */
    subscribeToOthersCalendarsEnabled: (state, nlGetters, rootState, rootGetters): boolean => {
        return rootGetters.permissionEnabled('calendar_subscriptions.subscribeToOthersCalendars');
    },

    /**
     * Default selectable calendar.
     *
     * @param state
     * @param myGetters
     */
    defaultCalendar: (state, myGetters) => {
        const primary = myGetters.primaryCalendar;
        if (!!primary && primary.selected && primary.visible) {
            return primary;
        }
        if (myGetters.selectedCalendars.length > 0) {
            return myGetters.selectedCalendars[0];
        }
        return myGetters.visibleCalendars.length > 0 ? myGetters.visibleCalendars[0] : null;
    },

    /**
     * Events formatted for Fullcalendar.
     *
     * @param state
     * @param myGetters
     */
    fullCalendarEvents: (state, myGetters) => {
        const toCal = (item: EventResource) => ({
            id: item.id,
            calendarId: item.calendarId,
            title: item.summary || 'Evento senza titolo',
            start: new Date(item.start),
            end: item.allDay ? moment(item.end).add(1, 'd').startOf('day').toDate() : new Date(item.end),
            allDay: item.allDay,
        });

        return myGetters.selectedCalendars
            .map((calendar: any) => ({
                color: calendar.backgroundColor,
                events: state.events
                    .filter((event) => calendar.id === event.calendarId)
                    .map((item: EventResource) => toCal(item)),
            }));
    },

    /**
     * Events formatted for Fullcalendar.
     *
     * @param state
     * @param myGetters
     * @param rootState
     * @param rootGetters
     */
    fullCalendarTimelineEvents: (state, myGetters, rootState, rootGetters) => {

        const toCal = (item: EventResource, resourceIds: number) => ({
            id: item.id,
            resourceIds: [resourceIds],
            calendarId: item.calendarId,
            title: item.summary || 'Evento senza titolo',
            type: item.type,
            typeDescription: item.typeDescription,
            typeExtendedDescription: item.typeExtendedDescription,
            start: new Date(item.start),
            end: item.allDay ? moment(item.end).add(1, 'd').startOf('day').toDate() : new Date(item.end),
            allDay: item.allDay,
            color: changeColorBasedOnType(item),
            textColor: 'black',
        });

        const toHoliday = (item: EventResource, user: UserResource) => ({
            id: item.id,
            resourceIds: [user.id],
            calendarId: item.calendarId,
            title: item.summary || 'Evento senza titolo',
            type: item.type,
            typeDescription: item.typeDescription,
            typeExtendedDescription: item.typeExtendedDescription,
            start: new Date(item.start),
            end: item.allDay ? moment(item.end).add(1, 'd').startOf('day').subtract(1, 's').toDate() : new Date(item.end),
            allDay: item.allDay,
            textColor: 'black',
            backgroundColor: '#EC7714',
            display: 'background'
        });
        let timelineEvents: any[] = [];
        let vacationEvents: [] = myGetters.selectedCalendars
            .map((calendar: any) => ({
                events: state.events
                    .filter((event) => calendar.id === event.calendarId)
                    .map((item: EventResource) => toCal(item, calendar.owner.id)),
            }));
            
        timelineEvents = vacationEvents;
        let users = [];
        let holidayEvents: any[] = [];
        // @ts-ignore
        users = rootState.users.users.length > 0 ? rootState.users.users : [rootState.user];
        users.forEach((user: UserResource) => {
            holidayEvents = myGetters.companiesCalendars
                .filter((calendar: any) => user.companies && user.companies.find((company: any) => company.id === calendar.companyId))
                .map((calendar: any) => ({
                    events: state.events
                        .filter((event) => calendar.id === event.calendarId)
                        .map((item: EventResource) => toHoliday(item, user))
                }));
                timelineEvents = [...timelineEvents, ...holidayEvents];
                if(holidayEvents[0]){
                    holidayEvents[0].events.push({
                        resourceIds: [user.id],
                        title: 'Weekend',
                        type: 'holidays',
                        startRecur: '2021-01-01 00:00:00',
                        daysOfWeek: [0,6],
                        backgroundColor: '#808080',
                        textColor: 'black',
                        display: 'background'
                    })
                }
                
        });
        
        return timelineEvents;

        function changeColorBasedOnType(event: any) {
            let color;
            if (event.vacation) {
                switch (event.vacation.status) {
                    case 'accepted':
                    case 'modified':
                        switch (event.type) {
                            case 'vacations_and_permissions':
                                color = event.typeDescription === 'permission' ? '#F0E68C' : '#00FF00'; // khaki (dark_yellow), green
                                break;
                            case 'mission':
                                color = '#FF6347'; // tomato
                                break;
                            case 'illness':
                                color = '#6A5ACD'; // slate blue
                                break;
                            case 'smart_working':
                                color = '#B0C4DE'; // light steel blue
                                break;
                            case 'not_paid_time_off':
                                color = '#FFB6C1'; // light pink
                                break;
                            case 'paid_time_off':
                                color = '#40FFC0'; // misali marine
                                break;
                            default:
                                color = 'blue';
                                break;
                        }
                        break;
                    case 'pending':
                        color = '#e6e6e6'; // light grey
                        break;
                }
            } else {
                switch (event.type) {
                    case 'vacations_and_permissions':
                        color = event.typeDescription === 'permission' ? '#F0E68C' : '#00FF00'; // khaki (dark_yellow), green
                        break;
                    case 'mission':
                        color = '#FF6347'; // tomato
                        break;
                    case 'illness':
                        color = '#6A5ACD'; // slate blue
                        break;
                    case 'smart_working':
                        color = '#B0C4DE'; // light steel blue
                        break;
                    case 'not_paid_time_off':
                        color = '#FFB6C1'; // light pink
                        break;
                    case 'paid_time_off':
                        color = '#40FFC0'; // misali marine
                        break;
                    default:
                        color = 'blue';
                        break;
                }
            }
            return color;
        }
    },

    /**
     * Return the specified event.
     *
     * @param state
     */
    getEventById: (state) => (id: number | string) => {
        return state.events.find((event) => event.id === Number(id));
    },

    /**
     * Return the specified event.
     *
     * @param state
     */
    getEventByVacationId: (state) => (id: number | string) => {
        return state.events.find((event) => event.vacation ? event.vacation.id === Number(id) : null);
    },

    /**
     * Return the specified calendar.
     *
     * @param state
     */
    getCalendarById: (state) => (id: string) => {
        return state.calendars.find((calendar) => calendar.id === id);
    },

    /**
     * Return the specified calendar.
     *
     * @param state
     */
    getCalendarByOwnerId: (state) => (id: number) => {
        return state.calendars.find((calendar) => calendar.owner.id === id);
    },

    /**
     * Get the http query params.
     *
     * @param state
     */

    eventQueryParams: (state) => ({
        start: state.eventFilters.start ? state.eventFilters.start.format('YYYY-MM-DD HH:mm:ss') : undefined,
        end: state.eventFilters.end ? state.eventFilters.end.format('YYYY-MM-DD HH:mm:ss') : undefined,
        attendee: state.eventFilters.attendee ? state.eventFilters.attendee : undefined,
    }),

    /**
     * Get the http query params.
     *
     * @param state
     */

    calendarQueryParams: (state) => ({
        types: state.calendarFilters.types ? state.calendarFilters.types : undefined,
    }),

    /**
     * Get the user primary calendar.
     *
     * @param state
     */
    primaryCalendar: (state) => state.calendars.find((calendar) => calendar.primary),

    /**
     * Calendars with flag hidden false and selected true.
     * @param state
     */
    selectedCalendars: (state) => state.calendars.filter((calendar) => calendar.selected && !calendar.hidden),

    /**
     * Calendars which are associated with a company
     * @param state
     */
    companiesCalendars: (state) => state.availableCalendars.filter((calendar) => !!calendar.companyId),

    /**
     * Get events with additonal info for timeline.
     *
     * @param state
     * @param myGetters
     */
    timelinedEvents: (state, myGetters) => state.events.map((event) => {
        return {
            ...event,
            calendar: myGetters.getCalendarById(event.calendarId),
        };
    }),

    /**
     * Get available calendars not subscribed yet.
     *
     * @param state
     * @param myGetters
     */
    unsubscribedCalendars: (state, myGetters) => state.availableCalendars.filter((calendar) => {
        return !myGetters.getCalendarById(calendar.id);
    }),

    /**
     * Calendars with flag hidden false.
     *
     * @param state
     */
    visibleCalendars: (state) => state.calendars.filter((calendar) => !calendar.hidden),
};
